import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, ShieldOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { openStripePortal } from '../lib/billing';
import { useAuth } from '../hooks/useAuth';

export const PremiumCancelPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [portalLoading, setPortalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const attemptPortalRedirect = useCallback(async () => {
    if (!user) {
      setPortalLoading(false);
      setError('Sign in to manage or cancel your subscription.');
      return;
    }

    setPortalLoading(true);
    setError(null);

    try {
      await openStripePortal(`${window.location.origin}/settings/subscription`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to open the billing portal.';
      setError(message);
      setPortalLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    void attemptPortalRedirect();
  }, [authLoading, attemptPortalRedirect]);

  if (portalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
        <div className="flex flex-col items-center gap-4 rounded-xl bg-background/80 px-8 py-10 shadow-xl">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <div className="text-center space-y-2">
            <h1 className="text-xl font-semibold text-foreground">Opening Stripe Portal…</h1>
            <p className="text-sm text-muted-foreground">
              We&apos;re redirecting you to Stripe so you can manage or cancel your subscription securely.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
      <div className="max-w-lg w-full rounded-xl bg-background/90 p-8 shadow-xl space-y-6">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-3">
            <ShieldOff className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Manage Subscription in Stripe</h1>
            <p className="text-sm text-muted-foreground">
              Cancelling happens securely in the Stripe Customer Portal.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="rounded-md bg-muted/50 p-4 text-sm text-muted-foreground">
          <p>
            Stripe handles all subscription changes, including cancellations and payment updates.
            After you finish in Stripe, you&apos;ll land back on Wallstreetle.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/settings')}
            className="w-full sm:w-auto"
          >
            Go to Settings
          </Button>
          <Button
            onClick={() => void attemptPortalRedirect()}
            className="w-full sm:w-auto"
            disabled={portalLoading}
          >
            Try Again
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
